'use client'

import { useState, useRef, useEffect, useCallback } from 'react'

// Función para pronunciar número de ticket dígito por dígito
const pronounceTicketNumber = (ticketNumber: string): string => {
  const digits: Record<string, string> = {
    '0': 'cero', '1': 'uno', '2': 'dos', '3': 'tres', '4': 'cuatro',
    '5': 'cinco', '6': 'seis', '7': 'siete', '8': 'ocho', '9': 'nueve'
  }
  
  return ticketNumber.split('').map(char => {
    if (char === '-') return ' guión '
    if (digits[char]) return ' ' + digits[char]
    return char.toLowerCase() // Para letras como T, K, etc.
  }).join('').trim()
}

// GLOBAL: Tracking de estado de ticket (evita problemas de closure en React)
declare global {
  interface Window {
    __ticketCollecting: boolean
    __ticketData: {
      userName: string
      branch: string
      priority: string
      description: string
    }
  }
}

if (typeof window !== 'undefined') {
  window.__ticketCollecting = false
  window.__ticketData = {
    userName: '',
    branch: '',
    priority: 'Media',
    description: ''
  }
}
import { X, Mic, Volume2 } from 'lucide-react'

interface VoiceAgentProps {
  isOpen: boolean
  onClose: () => void
}

interface HistoryMsg {
  role: 'user' | 'ai'
  text: string
}

interface TicketData {
  step: 'idle' | 'collecting' | 'ready'
  userName: string
  branch: string
  priority: 'Baja' | 'Media' | 'Alta'
  description: string
}

export default function VoiceAgent({ isOpen, onClose }: VoiceAgentProps) {
  const [isMounted, setIsMounted] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [response, setResponse] = useState('')
  const [error, setError] = useState('')
  const [conversation, setConversation] = useState<HistoryMsg[]>([])
  const [hasGreeted, setHasGreeted] = useState(false)
  const [voicesReady, setVoicesReady] = useState(false)
  const [ttsEnabled, setTtsEnabled] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const recognitionRef = useRef<any>(null)
  const synthRef = useRef<SpeechSynthesis | null>(null)
  const voicesRef = useRef<SpeechSynthesisVoice[]>([])
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)
  const transcriptBufferRef = useRef('')
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isSpeakingRef = useRef(false)
  
  // CRÍTICO: Usar ref para datos del ticket (evita problemas de closure)
  const ticketDataRef = useRef<TicketData>({
    step: 'idle',
    userName: '',
    branch: '',
    priority: 'Media',
    description: ''
  })

  const GOODBYE_PHRASES = [
    'adiós', 'adios', 'bye', 'nos vemos', 'hasta luego', 'hasta pronto',
    'chao', 'chau', 'me voy', 'ya me voy', 'ya terminé', 'ya termine',
    'ya no necesito', 'ya no quiero', 'ya está bien', 'ya esta bien',
    'no se resolvió', 'no resolvieron', 'no me ayudaron',
    'deja así', 'déjalo así', 'no importa', 'olvídalo', 'ya fue',
    'gracias ya terminé', 'gracias adios', 'muchas gracias bye',
    'ya no requiero ayuda', 'ya no necesito ayuda', 'puedo cerrar'
  ]

  const BUSY_PHRASES = [
    'dame un momento', 'un momento', 'espera', 'espérame', 'esperame',
    'dame tiempo', 'ya voy', 'ya casi', 'un segundo', 'un segundito',
    'déjame ver', 'dejame ver', 'dame unos minutos', 'déjame checar',
    'estoy ocupado', 'en un momento'
  ]
  
  // Detectar si es BUSY pero NO es despedida
  const isBusyButNotGoodbye = (text: string): boolean => {
    const lowerText = text.toLowerCase()
    // Excluir frases que son negaciones/despedidas
    if (lowerText.includes('no por') || lowerText.includes('no necesito') || 
        lowerText.includes('gracias') || lowerText.includes('hasta luego')) {
      return false
    }
    return BUSY_PHRASES.some(phrase => lowerText.includes(phrase))
  }

  // MEJORADO: Detección más precisa de intención de ticket
  const detectTicketIntent = (text: string): boolean => {
    const lowerText = text.toLowerCase()
    
    console.log('🎫 [DETECT] Analizando texto:', lowerText)
    
    // Excluir falsos positivos comunes
    const falsePositives = [
      'ticket empresarial', 'ticket de estacionamiento', 'ticket de compra',
      'boleto', 'comprobante', 'recibo', 'factura'
    ]
    
    if (falsePositives.some(fp => lowerText.includes(fp))) {
      console.log('⚠️ [DETECT] Falso positivo detectado, ignorando')
      return false
    }
    
    // Lista expandida de verbos de acción + "ticket"
    const actionVerbs = [
      'crear ticket', 'hacer ticket', 'generar ticket', 'abrir ticket',
      'levantar ticket', 'necesito un ticket', 'quiero un ticket',
      'puedes hacer un ticket', 'puedes crear un ticket', 'abre un ticket',
      'haz un ticket', 'crea un ticket', 'hazme un ticket', 'créame un ticket',
      'necesito que hagas un ticket', 'necesito que me hagas un ticket',
      'necesito hacer un ticket', 'quiero hacer un ticket', 'quiero crear un ticket',
      'por favor hacer un ticket', 'por favor crear un ticket',
      'ayuda con ticket', 'ayúdame con un ticket', 'necesito ayuda con ticket',
      'reportar problema', 'reportar incidente', 'crear reporte',
      'registrar ticket', 'registrar problema', 'registrar incidente'
    ]
    
    // Verificar cada verbo
    for (const verb of actionVerbs) {
      if (lowerText.includes(verb)) {
        console.log('🎫 [DETECT] Intención detectada: TRUE (encontrado:', verb, ')')
        return true
      }
    }
    
    console.log('🎫 [DETECT] Intención detectada: FALSE')
    return false
  }

  useEffect(() => { setIsMounted(true) }, [])

  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') return
    synthRef.current = window.speechSynthesis

    const loadVoices = () => {
      if (!synthRef.current) return
      const voices = synthRef.current.getVoices()
      if (voices.length > 0) {
        voicesRef.current = voices
        setVoicesReady(true)
        const spanishVoices = voices.filter((v: any) => v.lang.startsWith('es'))
        console.log('Voces disponibles:', voices.length)
      }
    }

    loadVoices()
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices
    }
    setTimeout(loadVoices, 500)
    setTimeout(loadVoices, 1000)
    setTimeout(loadVoices, 2000)

    return () => {
      if (synthRef.current) synthRef.current.cancel()
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      transcriptBufferRef.current = ''
    }
  }, [isMounted])

  // MEJORADO: Normalización más robusta
  const normalizeTechnicalTerms = (text: string): string => {
    let normalized = text
    // LUKS primero (antes de que se confunda con lux/deluxe)
    normalized = normalized.replace(/\bluks?\b/gi, 'LUKS')
    normalized = normalized.replace(/\blooks\b/gi, 'LUKS')
    normalized = normalized.replace(/\blluks\b/gi, 'LUKS')
    normalized = normalized.replace(/\blux\b/gi, 'LUKS')
    normalized = normalized.replace(/\bdeluxe\b/gi, 'LUKS')
    // Otros términos
    normalized = normalized.replace(/\bve pe ene\b/gi, 'VPN')
    normalized = normalized.replace(/\bvepeene\b/gi, 'VPN')
    normalized = normalized.replace(/\bsap be\b/gi, 'SAP B1')
    normalized = normalized.replace(/\bsap uno\b/gi, 'SAP B1')
    normalized = normalized.replace(/\bpos\b/gi, 'POS')
    normalized = normalized.replace(/\bti\b/gi, 'TI')
    return normalized
  }

  const removeEmojis = (text: string): string => {
    return text.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
  }

  const createTicketFromVoice = async (
    conversationText: string, 
    userName: string = 'Usuario Voice Agent',
    priority: string = 'Media',
    branch: string = 'N/A'
  ) => {
    console.log('🎫 [TICKET] Iniciando creación...')
    console.log('🎫 [TICKET] Usuario:', userName)
    console.log('🎫 [TICKET] Área:', branch)
    console.log('🎫 [TICKET] Prioridad:', priority)
    
    try {
      const requestBody = {
        title: `Soporte por voz - ITSM Evolbia - Área ${branch}`,
        description: conversationText,
        user: userName,
        priority: priority
      }
      
      const res = await fetch('/api/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })
      
      if (res.ok) {
        const data = await res.json()
        const ticketNumber = data.ticket?.number || data.ticket_number || 'TKT-DEMO'
        console.log('✅ [TICKET] Creado:', ticketNumber)
        return ticketNumber
      }
    } catch (err) {
      console.error('❌ [TICKET] Error:', err)
    }
    return null
  }

  const collectTicketData = async (text: string) => {
    console.log('🎫 [COLLECT] === INICIO collectTicketData ===')
    console.log('🎫 [COLLECT] Texto recibido:', text)
    console.log('🎫 [COLLECT] Estado GLOBAL:', window.__ticketCollecting)
    console.log('🎫 [COLLECT] Datos actuales:', window.__ticketData)
    
    // Verificación de seguridad
    if (!window.__ticketCollecting) {
      console.log('⚠️ [COLLECT] No estamos collecting, ignorando')
      return
    }
    
    const currentData = { ...window.__ticketData }
    const lowerText = text.toLowerCase()
    
    // PROCESAR UNO POR UNO según lo que falta
    
    // 1. Si falta el nombre, capturar TODO el texto como nombre
    if (!currentData.userName) {
      // Limpiar el texto de palabras comunes al inicio y al final
      let name = text
        .replace(/^(gracias|ok|vale|bien|perfecto|claro|sí|si|bueno)\s*/gi, '')
        .replace(/^(soy|me llamo|mi nombre es)\s+/i, '')
        .replace(/(mi área es|la área es|es la)\s*\d+.*$/gi, '')
        .trim()
      
      // Si el nombre tiene solo 1 palabra y es común, pedir confirmación
      const commonNames = ['salcedo', 'pérez', 'garcía', 'lopez', 'martínez']
      if (name.split(' ').length === 1 && commonNames.some(n => name.toLowerCase().includes(n))) {
        console.log('⚠️ [COLLECT] Nombre parece incompleto:', name)
      }
      
      // Si el nombre es muy corto o vacío, pedirlo de nuevo
      if (name.length < 3) {
        const reply = "Disculpa, no entendí tu nombre. ¿Podrías decirlo más claro?"
        setResponse(reply)
        setConversation(prev => [...prev, { role: 'ai', text: reply }])
        await speak(reply)
        return
      }
      
      currentData.userName = name
      console.log('✅ [COLLECT] Nombre capturado:', currentData.userName)
      
      // Actualizar y preguntar área
      window.__ticketData = currentData
      const reply = `Gracias ${name}. Ahora, ¿cuál es el número de tu área?`
      setResponse(reply)
      setConversation(prev => [...prev, { role: 'ai', text: reply }])
      await speak(reply)
      return
    }
    
    // 2. Si falta la área, extraer número
    if (!currentData.branch) {
      // Buscar número después de "área" o cualquier número
      const branchMatch = text.match(/(?:área|es la|la)\s*(\d+)/i) || text.match(/(\d+)/)
      if (branchMatch && branchMatch[1]) {
        currentData.branch = branchMatch[1]
        console.log('✅ [COLLECT] Área capturada:', currentData.branch)
        
        // Actualizar y preguntar prioridad
        window.__ticketData = currentData
        const reply = `Perfecto, área ${currentData.branch}. ¿Qué prioridad tiene el ticket? (baja, media, alta o urgente)`
        setResponse(reply)
        setConversation(prev => [...prev, { role: 'ai', text: reply }])
        await speak(reply)
      } else {
        const reply = "Disculpa, no entendí el número de área. ¿Podrías decir solo el número?"
        setResponse(reply)
        setConversation(prev => [...prev, { role: 'ai', text: reply }])
        await speak(reply)
      }
      return
    }
    
    // 3. Si falta la prioridad, detectar en el texto
    // SIEMPRE procesar la respuesta de prioridad
    {
      // Verificar también en la descripción original (primer mensaje)
      const fullText = currentData.description + ' ' + text
      const fullLowerText = fullText.toLowerCase()
      
      // Detectar urgencia del contexto completo
      if (fullLowerText.includes('urgente') || fullLowerText.includes('crítico') || fullLowerText.includes('critico') || 
          fullLowerText.includes('me urge') || fullLowerText.includes('es urgente') || 
          fullLowerText.includes('alta prioridad') || fullLowerText.includes('prioridad alta') ||
          fullLowerText.includes('por favor') && (fullLowerText.includes('llame') || fullLowerText.includes('contacte'))) {
        currentData.priority = 'Alta'
        console.log('✅ [COLLECT] Prioridad: Alta (detectada en descripción)')
      } else if (fullLowerText.includes('baja') || fullLowerText.includes('baja prioridad')) {
        currentData.priority = 'Baja'
        console.log('✅ [COLLECT] Prioridad: Baja')
      } else if (fullLowerText.includes('media') || fullLowerText.includes('normal')) {
        currentData.priority = 'Media'
        console.log('✅ [COLLECT] Prioridad: Media')
      } else {
        // Si no detecta, asumir Media
        console.log('⚠️ [COLLECT] Prioridad no detectada, usando Media')
      }
      
      // Actualizar y crear ticket
      window.__ticketData = currentData
      console.log('✅ [COLLECT] Todos los datos completos, creando ticket...')
      
      // CRÍTICO: Resetear ANTES de crear el ticket
      window.__ticketCollecting = false
      console.log('✅ [COLLECT] window.__ticketCollecting reseteado a false')
      
      await createTicketWithDetails()
      return
    }
  }

  const createTicketWithDetails = async () => {
    const data = window.__ticketData
    console.log('🎫 [CREATE] === INICIO createTicketWithDetails ===')
    console.log('🎫 [CREATE] Datos del ticket:')
    console.log('   - Nombre:', data.userName)
    console.log('   - Área:', data.branch)
    console.log('   - Prioridad:', data.priority)
    console.log('   - Descripción:', data.description)
    
    const fullDescription = `DATOS DEL USUARIO:\nNombre: ${data.userName}\nÁrea: ${data.branch}\nPrioridad: ${data.priority}\n\nDESCRIPCIÓN DEL PROBLEMA:\n${data.description}\n\nCONVERSACIÓN COMPLETA:\n${conversation.map(m => `${m.role === 'user' ? 'Usuario' : 'Agente'}: ${m.text}`).join('\n')}`
    
    console.log('🎫 [CREATE] Creando ticket con prioridad:', data.priority)
    const ticketNumber = await createTicketFromVoice(fullDescription, data.userName, data.priority, data.branch)
    
    // CRÍTICO: Resetear GLOBAL y ref ANTES de hablar
    window.__ticketCollecting = false
    window.__ticketData = {
      userName: '',
      branch: '',
      priority: 'Media',
      description: ''
    }
    ticketDataRef.current = {
      step: 'idle',
      userName: '',
      branch: '',
      priority: 'Media',
      description: ''
    }
    console.log('✅ [CREATE] Estado GLOBAL reseteado')
    
    if (ticketNumber) {
      // Construir mensaje SIN duplicación y con número pronunciable
      const pronounceableNumber = pronounceTicketNumber(ticketNumber)
      const parts = [`Ticket ${pronounceableNumber} creado exitosamente.`]
      
      if (data.userName && data.userName.trim()) parts.push(`Usuario: ${data.userName}`)
      if (data.branch && data.branch.trim()) parts.push(`Área: ${data.branch}`)
      if (data.priority) parts.push(`Prioridad: ${data.priority}`)
      
      const problemSummary = data.description ? data.description.substring(0, 80) + (data.description.length > 80 ? '...' : '') : ''
      if (problemSummary) parts.push(`Problema: ${problemSummary}`)
      
      parts.push('Un especialista te contactará pronto. ¿Necesitas algo más?')
      const ticketReply = parts.join('. ')
      
      console.log('🎫 [CREATE] Número pronunciable:', pronounceableNumber)
      setResponse(ticketReply)
      setConversation(prev => [...prev, { role: 'ai', text: ticketReply }])
      await speak(ticketReply)
    } else {
      const errorReply = "Lo siento, hubo un error al crear el ticket. Por favor intenta de nuevo o usa el chat de texto."
      setResponse(errorReply)
      setConversation(prev => [...prev, { role: 'ai', text: errorReply }])
      await speak(errorReply)
    }
  }

  const handleTicketRequest = async (text: string) => {
    console.log('🎫 [HANDLE] handleTicketRequest LLAMADO')
    console.log('🎫 [HANDLE] Texto:', text)
    
    const normalizedText = normalizeTechnicalTerms(text)
    setTranscript(normalizedText)
    setConversation(prev => [...prev, { role: 'user', text: normalizedText }])
    
    // CRÍTICO: Establecer estado GLOBAL inmediatamente
    window.__ticketCollecting = true
    // CRÍTICO: Guardar el problema original completo
    const originalProblem = normalizedText
    console.log('🎫 [HANDLE] Problema original guardado:', originalProblem)
    
    // DETECTAR prioridad desde el mensaje inicial
    const lowerProblem = originalProblem.toLowerCase()
    let initialPriority = 'Media'
    
    console.log('🎫 [HANDLE] Analizando prioridad en:', lowerProblem)
    
    // Detectar URGENCIA desde múltiples indicadores
    if (lowerProblem.includes('urgente') || 
        lowerProblem.includes('me urge') || 
        lowerProblem.includes('es urgente') ||
        lowerProblem.includes('crítico') || 
        lowerProblem.includes('critico') ||
        lowerProblem.includes('alta prioridad') ||
        lowerProblem.includes('rápido') || 
        lowerProblem.includes('rapido') ||
        lowerProblem.includes('inmediato') || 
        lowerProblem.includes('asap') ||
        lowerProblem.includes('lo antes posible') ||
        lowerProblem.includes('lo más pronto posible') ||
        lowerProblem.includes('necesito que alguien') ||
        lowerProblem.includes('necesito que me') ||
        (lowerProblem.includes('por favor') && (
          lowerProblem.includes('llame') || 
          lowerProblem.includes('contacte') || 
          lowerProblem.includes('comunique') ||
          lowerProblem.includes('proporcione') ||
          lowerProblem.includes('dé') ||
          lowerProblem.includes('de')
        ))) {
      initialPriority = 'Alta'
      console.log('✅ [HANDLE] Prioridad inicial detectada: ALTA')
    } else if (lowerProblem.includes('baja') || lowerProblem.includes('baja prioridad')) {
      initialPriority = 'Baja'
      console.log('✅ [HANDLE] Prioridad inicial detectada: BAJA')
    } else {
      console.log('⚠️ [HANDLE] Prioridad no detectada, usando MEDIA por defecto')
    }
    
    window.__ticketData = {
      userName: '',
      branch: '',
      priority: initialPriority,
      description: originalProblem
    }
    ticketDataRef.current = {
      step: 'collecting',
      userName: '',
      branch: '',
      priority: 'Media',
      description: normalizedText
    }
    
    console.log('✅ [HANDLE] Estado GLOBAL establecido:', window.__ticketCollecting)
    
    // PEDIR SOLO EL NOMBRE PRIMERO (uno por uno)
    const reply = "Perfecto, voy a crear un ticket para ti. Primero, ¿cuál es tu nombre completo?"
    
    console.log('🗣️ [HANDLE] Preguntando nombre:', reply)
    setResponse(reply)
    setConversation(prev => [...prev, { role: 'ai', text: reply }])
    await speak(reply)
  }

  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') return

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      setError('Tu navegador no soporta reconocimiento de voz.')
      return
    }

    recognitionRef.current = new SpeechRecognition()
    recognitionRef.current.continuous = true
    recognitionRef.current.interimResults = false
    recognitionRef.current.lang = 'es-MX'

    recognitionRef.current.onresult = (event: any) => {
      if (isSpeakingRef.current) return

      let text = event.results[event.results.length - 1][0].transcript.trim()
      text = normalizeTechnicalTerms(text)
      console.log('✅ Usuario dijo:', text)
      
      transcriptBufferRef.current += ' ' + text
      // Mostrar solo el texto actual (no acumulado)
      setResponse(text)
      
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      
      debounceTimerRef.current = setTimeout(() => {
        const fullText = transcriptBufferRef.current.trim()
        console.log('🎤 [VOICE] === DEBOUNCE TIMER EJECUTADO ===')
        console.log('🎤 [VOICE] Texto completo:', fullText)
        console.log('🎤 [VOICE] Longitud:', fullText.length)
        console.log('🎤 [VOICE] window.__ticketCollecting:', window.__ticketCollecting)
        console.log('🎤 [VOICE] ticketDataRef.step:', ticketDataRef.current.step)
        
        if (fullText && fullText.length > 3) {
          console.log('✅ [VOICE] Texto válido, procesando...')
          transcriptBufferRef.current = ''
          
          // CRÍTICO: Verificar GLOBAL primero
          if (window.__ticketCollecting) {
            console.log('✅ [VOICE] MODO TICKET ACTIVO - llamando a collectTicketData')
            collectTicketData(fullText)
            return
          }
          
          console.log('🎫 [VOICE] Modo normal, verificando intención...')
          
          const wantsTicket = detectTicketIntent(fullText)
          
          if (wantsTicket) {
            console.log('🎫 [VOICE] Intención detectada, llamando handleTicketRequest')
            handleTicketRequest(fullText)
          } else {
            console.log('🎫 [VOICE] Sin intención de ticket, backend normal')
            processVoiceCommand(fullText)
          }
        } else {
          console.log('⚠️ [VOICE] Texto muy corto o vacío, ignorando')
        }
      }, 800)  // 800ms después de hablar

      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)

      const isGoodbye = GOODBYE_PHRASES.some(phrase => text.toLowerCase().includes(phrase))
      const isBusy = isBusyButNotGoodbye(text)

      if (isGoodbye) {
        setTranscript(text)
        setConversation(prev => [...prev, { role: 'user', text }])
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
        transcriptBufferRef.current = ''
        handleGoodbye(text)
        return
      }

      if (isBusy) {
        setTranscript(text)
        setConversation(prev => [...prev, { role: 'user', text }])
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
        transcriptBufferRef.current = ''
        handleBusyResponse()
        return
      }
    }

    recognitionRef.current.onend = () => {
      setIsListening(false)
      if (!isSpeakingRef.current && ttsEnabled && !isClosing) {
        setTimeout(() => {
          try {
            recognitionRef.current.start()
            setIsListening(true)
          } catch (e) {}
        }, 300)
      }
    }

    recognitionRef.current.onerror = (event: any) => {
      if (event.error === 'no-speech') {
        console.log('No se detectó voz')
      } else {
        setError('Error de micrófono.')
      }
      setIsListening(false)
    }

    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop() } catch (e) {}
      }
    }
  }, [isMounted, ttsEnabled, isClosing])

  const processVoiceCommand = async (text: string) => {
    setTranscript(text)
    setConversation(prev => [...prev, { role: 'user', text }])
    await sendToVoiceAPI(text)
  }

  const handleBusyResponse = async () => {
    const reply = "Claro, tómate tu tiempo. Cuando estés listo, vuelve a hablar."
    setResponse(reply)
    setConversation(prev => [...prev, { role: 'ai', text: reply }])
    await speak(reply)
  }

  const handleGoodbye = async (userText: string) => {
    setIsClosing(true)
    setHasGreeted(false)

    const unresolved = ['no se resolvió', 'no resolvieron', 'no me ayudaron']
    const wasUnresolved = unresolved.some(p => userText.includes(p))

    let reply: string
    if (wasUnresolved) {
      reply = "Lamento que no hayamos podido resolver tu problema. Voy a generar un ticket. ¿Algo más antes de cerrar?"
    } else {
      reply = "Gracias por contactarme. ¡Hasta pronto!"
    }

    setResponse(reply)
    setConversation(prev => [...prev, { role: 'ai', text: reply }])

    const cleanText = removeEmojis(reply)
    if (synthRef.current) {
      synthRef.current.cancel()
      if (recognitionRef.current) {
        try { recognitionRef.current.stop() } catch (e) {}
      }

      const utterance = new SpeechSynthesisUtterance(cleanText)
      utterance.lang = 'es-MX'
      utterance.rate = 0.92

      const mxVoice = voicesRef.current.find((v: any) => v.lang === 'es-MX')
      if (mxVoice) utterance.voice = mxVoice

      utterance.onend = () => {
        setIsSpeaking(false)
        isSpeakingRef.current = false
        setTimeout(() => {
          onClose()
          setIsClosing(false)
          setTtsEnabled(false)
        }, 800)  // 800ms después de hablar
      }

      setIsSpeaking(true)
      isSpeakingRef.current = true
      synthRef.current.speak(utterance)
    }
  }

  useEffect(() => {
    if (isOpen && isMounted && !hasGreeted && ttsEnabled && voicesReady && !isClosing) {
      const greeting = "Hola, soy el agente de soporte de Evolbia. ¿En qué puedo ayudarte?"
      setTimeout(() => {
        setResponse(greeting)
        setConversation([{ role: 'ai', text: greeting }])
        speak(greeting)
        setHasGreeted(true)
      }, 500)
    }

    if (!isOpen) {
      setHasGreeted(false)
      setConversation([])
      setIsClosing(false)
    }
  }, [isOpen, isMounted, hasGreeted, ttsEnabled, voicesReady, isClosing])

  const normalizeForSpeech = (text: string): string => {
    return text
      .replace(/\bVPN\b/gi, 'V P N')
      .replace(/\bURL\b/gi, 'U R L')
      .replace(/\bIP\b/gi, 'I P')
      .replace(/\bLUKS\b/gi, 'lucs')
      .replace(/\*\*/g, '')
      .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const speak = useCallback(async (text: string) => {
    if (!synthRef.current || !text.trim()) return
    if (!ttsEnabled && !isClosing) return

    const cleanText = normalizeForSpeech(text)
    synthRef.current.cancel()
    
    if (recognitionRef.current) {
      try { recognitionRef.current.stop() } catch (e) {}
      setIsListening(false)
    }

    isSpeakingRef.current = true
    setIsSpeaking(true)

    const sentences = cleanText.match(/[^.!?]+[.!?]+/g) || [cleanText]
    let currentSentence = 0

    const speakNext = () => {
      if (currentSentence >= sentences.length) {
        setIsSpeaking(false)
        isSpeakingRef.current = false
        if (!isClosing && ttsEnabled) {
          setTimeout(() => {
            if (recognitionRef.current && !isSpeakingRef.current) {
              try {
                recognitionRef.current.start()
                setIsListening(true)
              } catch (e) {}
            }
          }, 800)  // 800ms después de hablar
        }
        return
      }

      const sentence = sentences[currentSentence].trim()
      if (!sentence) {
        currentSentence++
        speakNext()
        return
      }

      const utterance = new SpeechSynthesisUtterance(sentence)
      utterance.lang = 'es-MX'
      utterance.rate = 0.92
      utterance.pitch = 1.05

      const mxVoice = voicesRef.current.find((v: any) => v.lang === 'es-MX')
      if (mxVoice) utterance.voice = mxVoice

      utterance.onend = () => {
        currentSentence++
        setTimeout(speakNext, 200)  // 200ms entre oraciones
      }

      synthRef.current.speak(utterance)
    }

    speakNext()
  }, [ttsEnabled, isClosing])

  const sendToVoiceAPI = async (text: string) => {
    try {
      setError('')
      const res = await fetch('/api/voice/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: text,
          conversation_history: conversation.slice(-5)
        })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setResponse(data.reply)
      setConversation(prev => [...prev, { role: 'ai', text: data.reply }])
      await speak(data.reply)
    } catch (err) {
      setError('Error de conexión.')
    }
  }

  const enableTTSAndStart = () => {
    setTtsEnabled(true)
    setIsClosing(false)
  }

  const handleClose = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    if (recognitionRef.current) {
      try { recognitionRef.current.stop() } catch (e) {}
    }
    if (synthRef.current) {
      synthRef.current.cancel()
      setIsSpeaking(false)
      isSpeakingRef.current = false
    }
    setConversation([])
    setHasGreeted(false)
    setTtsEnabled(false)
    setIsClosing(false)
    onClose()
  }

  if (!isMounted || !isOpen) return null

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-3xl w-full p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition">
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Mic className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Agente de Voz de Evolbia ITSM</h2>
          <p className="text-gray-600">Conversación natural con el agente de soporte</p>
          <img src="/logos/logo.png" alt="Evolbia" className="h-20 w-auto object-contain mx-auto my-4" />
        </div>

        {!ttsEnabled && (
          <div className="mb-6 p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
            <p className="text-blue-900 font-semibold mb-3">Activa la voz para comenzar</p>
            <button onClick={enableTTSAndStart} disabled={!voicesReady} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition shadow-lg font-semibold text-lg">
              <Volume2 className="w-6 h-6" />
              {voicesReady ? 'Activar Voz y Comenzar' : 'Cargando voces...'}
            </button>
          </div>
        )}

        <div className="text-center mb-6">
          {isListening && !isSpeaking && !isClosing && (
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-green-100 text-green-700 rounded-full shadow-md animate-pulse">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="font-semibold">Escuchándote...</span>
            </div>
          )}
          {isSpeaking && (
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 text-blue-700 rounded-full shadow-md">
              <Volume2 className="w-5 h-5 animate-pulse" />
              <span className="font-semibold">Agente respondiendo...</span>
            </div>
          )}
        </div>

        {conversation.length > 0 && (
          <div className="mb-6 space-y-3 max-h-64 overflow-y-auto">
            {conversation.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                  <p className="text-sm">{msg.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {transcript && (
          <div className="mb-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
            <p className="text-xs text-blue-600 mb-1 font-semibold">Tú dijiste:</p>
            <p className="text-gray-900">{transcript}</p>
          </div>
        )}

        {response && (
          <div className="mb-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
            <p className="text-xs text-green-600 mb-1 font-semibold">Agente responde:</p>
            <p className="text-gray-900">{response}</p>
          </div>
        )}
      </div>
    </div>
  )
}
